import { Board } from './black_hole_game.ts';

export type ShareKind = 'puzzle' | 'replay';

// Piece values encode chronology, so the link contains only starting moves.
// Base-36 cell indices keep URLs short without storing solver results or hints.
export function encodeBoard(board: Board): string {
    if (board.rows !== 5 && board.rows !== 6) throw new Error('This board size cannot be shared.');
    const moves: number[] = [];
    for (let row = 0; row < board.rows; row++) {
        for (let col = 0; col <= row; col++) {
            const value = board.at(row, col);
            if (value !== 0) {
                const ply = 2 * (Math.abs(value) - 1) + (value < 0 ? 1 : 0);
                if (!Number.isInteger(ply) || ply < 0 || ply >= board.filled || moves[ply] !== undefined) {
                    throw new Error('This position cannot be shared.');
                }
                moves[ply] = row * (row + 1) / 2 + col;
            }
        }
    }
    if (moves.length !== board.filled || Array.from({ length: board.filled }, (_, i) => moves[i]).some(move => move === undefined)) {
        throw new Error('This position cannot be shared.');
    }
    const payload = `1.${board.rows}.${moves.map(move => move.toString(36)).join('')}`;
    if (!decodeBoard(payload)) throw new Error('This position cannot be shared.');
    return payload;
}

export function decodeBoard(payload: string): Board | null {
    if (payload.length > 25 || !/^1\.[56]\.[0-9a-k]*$/.test(payload)) return null;
    const rows = Number(payload[2]);
    const moves = payload.slice(4);
    let board = new Board(rows);
    if (moves.length > board.cellCount - 1) return null;
    try {
        for (const char of moves) {
            const index = parseInt(char, 36);
            if (index >= board.cellCount) return null;
            const row = Math.floor((Math.sqrt(8 * index + 1) - 1) / 2);
            board = board.play(row, index - row * (row + 1) / 2);
        }
        return board;
    } catch {
        return null;
    }
}

export function makeShareUrl(kind: ShareKind, board: Board): string {
    const url = new URL(window.location.href);
    url.hash = `/${kind}/${encodeBoard(board)}`;
    return url.toString();
}

export async function shareBoard(kind: ShareKind, board: Board): Promise<'shared' | 'copied'> {
    const url = makeShareUrl(kind, board);
    if (navigator.share) {
        try {
            await navigator.share({ title: 'Black Hole', text: kind === 'puzzle' ? 'Can you find the best finish?' : 'Watch this Black Hole game.', url });
            return 'shared';
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') throw new Error('Sharing cancelled.', { cause: error });
            // A browser can expose sharing without supporting it in this context.
        }
    }
    try {
        await navigator.clipboard.writeText(url);
        return 'copied';
    } catch (error) {
        throw new Error('Could not copy the link. Try sharing from a browser with clipboard access.', { cause: error });
    }
}
